import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Collection, collectionTitles, ToptenCollectionType } from '../../models/collection';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';
import { Util } from 'src/app/util';
import { Record } from '../../models/record';
import { OtkList } from '../../models/user-item-list';
import { LocaleService } from '../../services/locale.service';

@Injectable({
  providedIn: 'root',
})
export class CollectionsService {
  private readonly http = inject(HttpClient);
  private readonly localeService = inject(LocaleService);

  getCollections(locale: string): Observable<Collection[]> {
    const url = `${environment.apiUrl}/records/collections`;
    const params = {
      params: { showExperiences: 'true', locale },
    };

    return this.http
      .get<Collection[]>(url, params)
      .pipe(map((collections) => collections.map((collection) => this.mapCollection(collection))));
  }

  private mapCollection(collection: Collection): Collection {
    const fromUserList = collection.topten === ToptenCollectionType.GENERIC_BY_LIST_SELECTION;
    const userLists = collection.userLists?.map((userList) => this.mapUserLists(userList)) ?? [];

    return {
      ...collection,
      fromUserList,
      items: this.mapItems(fromUserList, userLists, collection.records),
      name: this.getCollectionName(collection),
      records: collection.records.map((record) => Util.prepareRecord(record)),
      userLists: [],
    };
  }

  private getCollectionName(collection: Collection): string {
    if (!collection.name) {
      return collectionTitles[collection.topten];
    }

    if (Object.keys(collection.topTenTitles).includes(this.localeService.language)) {
      return collection.topTenTitles[this.localeService.language].text;
    }

    if (Object.keys(collection.topTenTitles).includes(Util.DEFAULT_LANGUAGE_CODE_FOR_CAROUSELS)) {
      return collection.topTenTitles[Util.DEFAULT_LANGUAGE_CODE_FOR_CAROUSELS].text;
    }

    return collection.name;
  }

  private mapUserLists(userList: OtkList): OtkList {
    return {
      ...userList,
      covers: Util.moveSelectedCoverToTop(
        userList.items.map((item) => item.coverUrl),
        userList.selectedCoverIdx
      ),
    };
  }

  private mapItems(fromUserList: boolean, userLists: OtkList[], records: Record[]): OtkList[] | Record[] {
    if (fromUserList) {
      return userLists;
    }

    return records.filter((record) => record.active);
  }
}
